import { PureComponent } from 'react';
import { withRouter, Route } from 'react-router-dom';
import { connect } from 'react-redux';
import { push as changeLocation } from 'connected-react-router';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Button, Spinner, Position } from '@blueprintjs/core';

import { findRequests } from '@f/services/batch-search/batch-search.actions';
import { findUsers } from '@f/services/administration/administration.actions';
import Table from '@f/components/shared/table/Table';
import BatchSearchStatusTag from '@f/components/shared/batch-search-status-tag/BatchSearchStatusTag';
import { SearchDetails } from '@f/components/shared/search-details/SearchDetails';

import ItemDrawerPage from './pages/item-drawer/ItemDrawerPage';
import cls from './BatchSearchRequestListPage.module.css';
import { BatchSearchRequestPriority } from './components/BatchSearchRequestPriority';
import { BatchSearchRequestExport } from './components/BatchSearchRequestExport';

class BatchRequestListPage extends PureComponent {
  static propTypes = {
    match: PropTypes.object,
    requests: PropTypes.object,
    isFindingRequests: PropTypes.bool,
    findingRequestsError: PropTypes.object,
    changeLocation: PropTypes.func,
    findRequests: PropTypes.func,
  };

  dataChunkSize = 20;

  schema = [
    {
      label: 'STATUS',
      renderer: item => (
        <BatchSearchStatusTag large minimal status={item.status} removed={item.isRemoved} />
      ),
    },
    {
      label: 'ID',
      renderer: 'id',
      cellProps: {
        className: cls.primary,
      },
    },
    {
      label: 'INITIATOR',
      grow: true,
      renderer: item => (item.owner ? item.owner.email : <Spinner size={16} />),
    },
    {
      label: 'SIZE',
      renderer: 'size',
    },
    {
      label: 'PRIORITY',
      renderer: item => <BatchSearchRequestPriority batch={item} />,
    },
    {
      label: 'CONSUMED CREDITS',
      renderer: item =>
        item.creditsDue != null ? BigInt(item.creditsDue).toLocaleString() : 'n/a',
    },
    {
      label: 'CREATED AT',
      renderer: item => new Date(item.createdAt).toLocaleString(),
    },

    {
      label: '',
      renderer: item => {
        return (
          <SearchDetails
            search={item}
            tooltipPosition={Position.LEFT}
            triggerElement={<Button icon="small-info-sign" minimal />}
            hideStartingMaterialDatasetsNames
          />
        );
      },
    },
  ];

  state = {
    users: {},
    dataInfo: {
      total: 0,
      skip: 0,
      ids: [],
    },
  };

  componentDidMount() {
    this.onFindItems();
  }

  componentDidUpdate(prevProps, prevState) {
    const { dataInfo } = this.state;
    const { dataInfo: prevDataInfo } = prevState;

    const newRequestIds = _.difference(dataInfo.ids, prevDataInfo.ids);
    const haveRequestsChanged = newRequestIds.length > 0;

    if (haveRequestsChanged) {
      this.updateMetadata(newRequestIds);
    }
  }

  updateMetadata = async requestIds => {
    const { requests } = this.props;

    const ownerIds = _.chain(requests)
      .pick(requestIds)
      .values()
      .map(request => request.ownerId)
      .uniq()
      .value();

    // TODO refactor: fetch relation with requests
    const users = await findUsers(ownerIds);
    this.setState(prevState => ({
      users: {
        ...prevState.users,
        ...users,
      },
    }));
  };

  buildItems = () => {
    const { requests } = this.props;
    const { dataInfo, users } = this.state;
    const { ids } = dataInfo;

    return _.chain(ids)
      .map(id => requests[id])
      .filter(Boolean)
      .map(request => ({
        ...request,
        owner: users[request.ownerId],
      }))
      .value();
  };

  onFindItems = async () => {
    const { dataInfo } = this.state;
    const { skip, ids } = dataInfo;

    const dataInfoChunk = await this.props.findRequests(
      {
        orderBy: [
          {
            createdAt: 'desc',
          },
        ],
        skip,
        take: this.dataChunkSize,
      },
      {
        findAll: true,
      }
    );

    this.setState({
      dataInfo: {
        total: dataInfoChunk.total,
        skip: skip + this.dataChunkSize,
        ids: _.chain(ids).concat(dataInfoChunk.ids).uniq().value(),
      },
    });
  };

  onNavigateToItem = item => {
    const { match } = this.props;
    this.props.changeLocation(`${match.path}/${item.id}`);
  };

  render() {
    const { match, isFindingRequests } = this.props;
    const { dataInfo } = this.state;
    const { total, skip } = dataInfo;

    const items = this.buildItems();

    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <h2 className={cls.title}>Search requests</h2>

          <span className={cls.pagination}>
            {'Showing '}
            {items.length}
            {' of '}
            {total}
          </span>

          <BatchSearchRequestExport />
        </div>

        <Table
          className={cls.list}
          schema={this.schema}
          items={items}
          interactive
          loading={isFindingRequests}
          loadingCaption="Loading searches..."
          emptyStateProps={{
            title: 'No searches',
            description: 'There are no searches here yet.',
          }}
          onRowClick={this.onNavigateToItem}
        >
          {skip < total && (
            <Table.After className={cls.footer}>
              <Button
                large
                minimal
                disabled={isFindingRequests}
                loading={isFindingRequests}
                text="Show more searches"
                icon="double-chevron-down"
                rightIcon="double-chevron-down"
                onClick={this.onFindItems}
              />
            </Table.After>
          )}
        </Table>

        <Route path={`${match.path}/:requestId`} component={ItemDrawerPage} />
      </div>
    );
  }
}

function mapStateToProps(state) {
  const requestState = state.batchSearch.request;
  const requests = requestState.data;
  const isFindingRequests = requestState.find.processing > 0;
  const findingRequestsError = requestState.find.error;

  return {
    requests,
    isFindingRequests,
    findingRequestsError,
  };
}

export default withRouter(
  connect(mapStateToProps, { changeLocation, findRequests })(BatchRequestListPage)
);
